/**
 * The Candidates tab's ways in — a pasted list, Recruiter's "Save to PDF" files, and (Oct 2026) the
 * profile open in Recruiter, read by the extension — plus "Get profile" for someone the sourcing
 * agent found, whose profile text is not read yet. Every path ends the same way: the new people
 * are merged into the list and, when the brief exists, checked one at a time.
 */
import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';
import { captureInExtension, captureOpenTab, explainCapture } from './outreachExtension';
import type { OutreachImportBox } from './OutreachImportBox';

type ImportSummary = Parameters<typeof OutreachImportBox>[0]['lastResult'];

/** New people are appended; people who only gained their Recruiter id are replaced in place. */
export function mergeImported(list: OutreachCandidate[], r: api.ImportResult): OutreachCandidate[] {
  const updated = new Map((r.updated || []).map((u) => [u.id, u]));
  return [...list.map((c) => updated.get(c.id) || c), ...r.items];
}

const toastError = (err: unknown, fallback: string) =>
  showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : fallback });

export function useOutreachImport({
  postingId,
  roleTitle,
  hasBrief,
  setItems,
  replace,
  checkAll,
}: {
  postingId: string;
  roleTitle: string;
  hasBrief: boolean;
  setItems: (fn: (list: OutreachCandidate[]) => OutreachCandidate[]) => void;
  replace: (item: OutreachCandidate) => void;
  /** Check these ids against the brief, one by one (the view's sequential loop). */
  checkAll: (ids: string[]) => void;
}) {
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportSummary>(null);
  const [fileProgress, setFileProgress] = useState<{ done: number; total: number } | null>(null);
  const [capturing, setCapturing] = useState<Set<string>>(new Set());

  async function importCsv(csv: string) {
    setImporting(true);
    try {
      const r = await api.importOutreach(postingId, roleTitle, csv);
      setImportResult({ ...r, idsAdded: (r.updated || []).map((u) => u.name) });
      setItems((list) => mergeImported(list, r));
      void logAnalyticsEvent('ta_outreach_imported', { rows: r.imported, skipped: r.skipped.length, source: 'csv' });
      if (hasBrief) checkAll(r.items.map((i) => i.id));
    } catch (err) {
      toastError(err, 'Could not import.');
    } finally {
      setImporting(false);
    }
  }

  async function importFiles(files: File[]) {
    setImporting(true);
    const total = { imported: 0, skipped: [] as { row: number | null; reason: string }[], truncated: false, idsAdded: [] as string[] };
    const newIds: string[] = [];
    try {
      const pdfs: { name: string; data: string }[] = [];
      for (const f of files) {
        const data = await api.fileToBase64(f);
        if (/\.zip$/i.test(f.name) || f.type === 'application/zip') pdfs.push(...(await api.unzipOutreach(data)).files);
        else if (/\.pdf$/i.test(f.name) || f.type === 'application/pdf') pdfs.push({ name: f.name, data });
        else total.skipped.push({ row: null, reason: `${f.name}: not a ZIP or PDF` });
      }
      setFileProgress({ done: 0, total: pdfs.length });
      for (const [i, pdf] of pdfs.entries()) {
        try {
          const r = await api.importOutreachPdf(postingId, roleTitle, pdf.name, pdf.data);
          total.imported += r.imported;
          total.skipped.push(...r.skipped);
          newIds.push(...r.items.map((x) => x.id));
          total.idsAdded.push(...(r.updated || []).map((u) => u.name));
          setItems((list) => mergeImported(list, r));
        } catch (err) {
          total.skipped.push({ row: null, reason: `${pdf.name}: ${err instanceof Error ? err.message : 'could not be read'}` });
        }
        setFileProgress({ done: i + 1, total: pdfs.length });
      }
      setImportResult(total);
      void logAnalyticsEvent('ta_outreach_imported', { rows: total.imported, skipped: total.skipped.length, source: 'pdf' });
    } catch (err) {
      toastError(err, 'Could not read those files.');
    } finally {
      setFileProgress(null);
      setImporting(false);
    }
    if (hasBrief && newIds.length) checkAll(newIds);
  }

  /** "Add the profile open in Recruiter": the extension reads that tab, the proxy runs the usual checks. */
  async function importOpenTab() {
    setImporting(true);
    try {
      const read = await captureOpenTab();
      if (!read.ok || !read.url || !read.text) throw new Error(explainCapture(read));
      const r = await api.importCapture(postingId, roleTitle, {
        url: read.url,
        text: read.text,
        name: read.name,
        headline: read.headline,
        location: read.location,
        publicUrl: read.publicUrl,
        expanded: read.expanded,
        markersLeft: read.markersLeft,
      });
      setImportResult({ ...r, truncated: false, idsAdded: (r.updated || []).map((u) => u.name) });
      setItems((list) => mergeImported(list, r));
      void logAnalyticsEvent('ta_outreach_imported', { rows: r.imported, skipped: r.skipped.length, source: 'open_tab' });
      if (hasBrief) checkAll(r.items.map((i) => i.id));
    } catch (err) {
      toastError(err, 'Could not read the open profile.');
    } finally {
      setImporting(false);
    }
  }

  /** "Get profile": read one candidate's Recruiter profile in a background tab and store the text. */
  async function captureOne(c: OutreachCandidate): Promise<OutreachCandidate | null> {
    if (!c.recruiterId) return null;
    setCapturing((s) => new Set(s).add(c.id));
    try {
      const read = await captureInExtension(c.recruiterId);
      if (!read.ok || !read.url || !read.text) throw new Error(`${c.name}: ${explainCapture(read)}`);
      const { item } = await api.saveCapture(c.id, { url: read.url, text: read.text, name: read.name, headline: read.headline, location: read.location, publicUrl: read.publicUrl, expanded: read.expanded, markersLeft: read.markersLeft });
      replace(item);
      void logAnalyticsEvent('ta_outreach_profile_captured', { via: 'extension' });
      return item;
    } catch (err) {
      toastError(err, 'Could not read that profile.');
      return null;
    } finally {
      setCapturing((s) => {
        const next = new Set(s);
        next.delete(c.id);
        return next;
      });
    }
  }

  return { importing, importResult, fileProgress, capturing, importCsv, importFiles, importOpenTab, captureOne };
}
